import type { TFunction } from 'i18next';
import type { ProviderContext } from 'notistack';

function fallbackCopyTextToClipboard(text: string): void {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    // The below method is deprecated, but we keep it just in case
    // user firing it has a legacy browser and it still works for him.
    document.execCommand('copy');
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: logging error in console
    console.error('Unable to copy', err);
  }

  document.body.removeChild(textArea);
}

export async function copyToClipboard(text: string): Promise<void> {
  // eslint-disable-next-line baseline-js/use-baseline
  if (navigator.clipboard) {
    try {
      // eslint-disable-next-line baseline-js/use-baseline
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Clipboard API failed (permissions, iframe, etc.) -- use fallback
    }
  }
  fallbackCopyTextToClipboard(text);
}

export const copyToClipboardWithConfirmation = (
  copiedContent: string,
  enqueueSnackbar: ProviderContext['enqueueSnackbar'],
  t: TFunction
): void => {
  copyToClipboard(copiedContent)
    .then(() => {
      enqueueSnackbar(t('Content copied to your clipboard'), {
        variant: 'success',
      });
    })
    .catch(() => {
      enqueueSnackbar(t('Failed to copy content to your clipboard'), {
        variant: 'error',
      });
    });
};

type RichClipboardContent = {
  plainText: string;
  htmlText: string;
};

export async function copyRichTextToClipboard({ plainText, htmlText }: RichClipboardContent): Promise<void> {
  // eslint-disable-next-line baseline-js/use-baseline
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      // eslint-disable-next-line baseline-js/use-baseline
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      });

      // eslint-disable-next-line baseline-js/use-baseline
      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch {
      // Clipboard API failed (permissions, unsupported type, iframe, etc.) -- use plain text fallback
    }
  }

  await copyToClipboard(plainText);
}
